<template>
  <div class="skill-page proxy-page">
    <div class="proxy-wrap">
      <!-- ==================== 头部 ==================== -->
      <header class="hero">
        <span class="hero-badge">Vite Proxy 配置详解</span>
        <h1 class="hero-title">代理配置总结</h1>
        <p class="hero-sub">
          <code>/dev-api</code>、<code>target</code>、<code>rewrite</code>、<code>changeOrigin</code>
          四者到底什么关系？一句话：它们各干各的，互相之间<b>没有“替换”关系</b>。
        </p>
        <div class="hero-formula">
          <span class="fx-key">最终 URL</span>
          <span class="fx-eq">=</span>
          <span class="fx-target">target</span>
          <span class="fx-plus">+</span>
          <span class="fx-path">rewrite 处理后的 path</span>
        </div>
      </header>

      <!-- ==================== 01 核心结论 ==================== -->
      <section class="panel">
        <h2 class="sec-title"><span class="sec-no">01</span>核心结论</h2>
        <p class="sec-lead">
          <b>两种猜测都不对。</b>代理过程有<b>三项配置</b>，它们各干各的事，互相之间<b>没有“替换”关系</b>：
        </p>
        <div class="table-wrap">
          <table class="cfg-table">
            <thead>
              <tr><th>配置项</th><th>值</th><th>作用</th></tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in configRows" :key="i">
                <td class="cfg-key">{{ r.key }}</td>
                <td><code>{{ r.value }}</code></td>
                <td>{{ r.role }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul class="note-list">
          <li><b>/dev-api</b> 是被<b>删掉</b>的（<code>replace(..., '')</code> 第二个参数是空字符串），不是变成 target</li>
          <li><code>http://localhost:80</code> 从来不在 <code>path</code> 里，跟 <code>rewrite</code> 无关，<b>谈不上整段替换</b></li>
        </ul>
      </section>

      <!-- ==================== 02 四步流水线 ==================== -->
      <section class="panel">
        <h2 class="sec-title"><span class="sec-no">02</span>完整的四步流水线</h2>
        <div class="req-line">
          <span class="req-label">浏览器原始请求</span>
          <code class="req-url">http://localhost/dev-api/admin-api/system/auth/login?x=1</code>
        </div>
        <div class="req-break">
          <span class="brk brk-proto">协议 + 域名 + 端口</span>
          <span class="brk brk-path">路径 path</span>
          <span class="brk brk-query">query</span>
        </div>

        <div class="stepper">
          <button
            v-for="(s, i) in steps"
            :key="s.key"
            class="step-tab"
            :class="{ active: activeStep === i, done: activeStep > i }"
            :style="activeStep === i ? { '--accent': s.accent } : null"
            type="button"
            @click="activeStep = i"
          >
            <span class="step-badge">{{ s.no }}</span>
            <span class="step-icon">{{ s.icon }}</span>
            <span class="step-name">{{ s.title }}</span>
          </button>
        </div>

        <div class="step-detail" :style="{ '--accent': currentStep.accent }">
          <div class="step-detail-head">
            <span class="step-detail-no">第 {{ currentStep.no }} 步</span>
            <span class="step-detail-title">{{ currentStep.title }}</span>
          </div>
          <p v-for="(p, i) in currentStep.paragraphs" :key="i" class="step-p">{{ p }}</p>
          <pre class="code-block"><code>{{ currentStep.code }}</code></pre>
          <div class="io-row">
            <div class="io-box io-in">
              <span class="io-tag">输入</span>
              <code>{{ currentStep.input }}</code>
            </div>
            <div class="io-arrow">→</div>
            <div class="io-box io-out">
              <span class="io-tag">输出</span>
              <code>{{ currentStep.output }}</code>
            </div>
          </div>
        </div>
      </section>

      <!-- ==================== 03 两种错误猜测 ==================== -->
      <section class="panel">
        <h2 class="sec-title"><span class="sec-no">03</span>两种错误猜测为什么错</h2>
        <div class="wrong-grid">
          <div v-for="(w, i) in wrongGuesses" :key="i" class="wrong-card">
            <div class="wrong-head">
              <span class="wrong-x">✕</span>
              <span class="wrong-title">{{ w.title }}</span>
            </div>
            <div class="wrong-reason">{{ w.reason }}</div>
            <ul class="wrong-points">
              <li v-for="(p, j) in w.points" :key="j">{{ p }}</li>
            </ul>
          </div>
        </div>
      </section>

      <!-- ==================== 04 快递类比 ==================== -->
      <section class="panel">
        <h2 class="sec-title"><span class="sec-no">04</span>快递类比（一次性记住）</h2>
        <div class="table-wrap">
          <table class="express-table">
            <thead>
              <tr><th>代码</th><th>类比</th></tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in expressRows" :key="i">
                <td class="ex-code"><code>{{ r.code }}</code></td>
                <td>{{ r.analogy }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="memo-box">
          <b>标签</b>（<code>/dev-api</code>）用来分拣，撕掉；<b>仓库地址</b>（<code>target</code>）用来指路，加最前面；<b>门牌号</b>（<code>/admin-api/...</code>）货真价实，一路保留。<br />
          <span class="memo-em">没有任何一步叫“把标签换成仓库地址”。</span>
        </div>
      </section>

      <!-- ==================== 05 三者关系 ==================== -->
      <section class="panel">
        <h2 class="sec-title"><span class="sec-no">05</span>补充：target / changeOrigin / rewrite 的 ^ 三者关系</h2>
        <ul class="relation-list">
          <li v-for="(r, i) in relationItems" :key="i">
            <span class="rel-dot" :style="{ background: r.color }"></span>
            <span class="rel-text" v-html="r.html"></span>
          </li>
        </ul>
      </section>

      <!-- ==================== 06 排查口诀 ==================== -->
      <section class="panel">
        <h2 class="sec-title"><span class="sec-no">06</span>实用排查口诀</h2>
        <p class="sec-lead">配代理出问题，按顺序查这三项：</p>
        <ol class="check-list">
          <li v-for="(c, i) in checklist" :key="i" class="check-item">
            <span class="check-order">{{ i + 1 }}</span>
            <div class="check-body">
              <div class="check-q">{{ c.q }}</div>
              <div class="check-a">{{ c.a }}</div>
            </div>
          </li>
        </ol>
        <div class="foot-note">
          注：本项目 <code>vite.config.ts:39-41</code> 里 <code>proxy.on('error', () =&gt; {})</code>
          把错误吞掉了（为避免后端没启动时刷屏），因此排查时需临时注释掉才看得到真实错误。
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProxyConfigSummary',
  data() {
    return {
      activeStep: 0,
      steps: [
        {
          key: 'sort',
          no: 1,
          icon: '🔍',
          title: '分拣（看键）',
          accent: '#409EFF',
          paragraphs: [
            'vite 拿到 path = /dev-api/admin-api/system/auth/login，去 proxy 的键里找：谁是这个路径的开头？',
            "'/dev-api' ✅ 匹配上了 → 这条请求交给我处理。",
            '这一步只做判断，不做任何修改。键永远不会出现在转发出去的 URL 里。它不是“被替换成 target”，它是“只看路径前缀来挑选请求的匹配规则”。'
          ],
          code:
            "// 只判断，不修改\npath 前缀 = /dev-api\n匹配 proxy 键 '/dev-api'   ✅ 命中 → 交给本条规则处理",
          input: 'path = /dev-api/admin-api/system/auth/login',
          output: '（命中本条规则，交给下一步）'
        },
        {
          key: 'rewrite',
          no: 2,
          icon: '✂️',
          title: '剪掉前缀（rewrite）',
          accent: '#E6A23C',
          paragraphs: [
            "用 rewrite 把前缀 /dev-api 剪掉，替换为 ''（空字符串）。",
            '输入 /dev-api/admin-api/system/auth/login → 剪掉 /dev-api → 输出 /admin-api/system/auth/login。',
            "剪掉的那一段什么都没留下，'' 就是“什么都不写”，它没有变成 target。"
          ],
          code:
            "path.replace(/^\\/dev-api/, '')\n// 第二个参数是空字符串 → 等于“删除前缀”\n\n// 输入： /dev-api/admin-api/system/auth/login\n//                ↓\n// 输出： /admin-api/system/auth/login",
          input: '/dev-api/admin-api/system/auth/login',
          output: '/admin-api/system/auth/login'
        },
        {
          key: 'join',
          no: 3,
          icon: '🔗',
          title: '拼接（target + path）',
          accent: '#67C23A',
          paragraphs: [
            '把 target 加在最前面，接上第 2 步处理后的 path。',
            'target = http://192.168.1.21:55055，path = /admin-api/system/auth/login。',
            '拼接后得到最终要请求的地址。'
          ],
          code:
            "target = http://192.168.1.21:55055\npath   = /admin-api/system/auth/login\n         ↓ 拼接\n最终   = http://192.168.1.21:55055/admin-api/system/auth/login?x=1",
          input: 'http://192.168.1.21:55055 + /admin-api/system/auth/login',
          output: 'http://192.168.1.21:55055/admin-api/system/auth/login?x=1'
        },
        {
          key: 'host',
          no: 4,
          icon: '🏷️',
          title: '改 Host 头（changeOrigin）',
          accent: '#909399',
          paragraphs: [
            'changeOrigin 为 true 时，把请求头里的 Host 改成目标主机的名字。',
            'Host: localhost → Host: 192.168.1.21:55055。',
            '只改这一行头，Cookie / Authorization / User-Agent 等原样透传。'
          ],
          code:
            "// changeOrigin: true\nHost: localhost\n         ↓\nHost: 192.168.1.21:55055",
          input: 'Host: localhost',
          output: 'Host: 192.168.1.21:55055'
        }
      ],
      configRows: [
        { key: "① 键 '/dev-api'", value: '匹配规则', role: '判断“这条请求归不归我管”，不会进入最终 URL' },
        { key: '② target', value: 'http://192.168.1.21:55055', role: '决定“往哪连”' },
        { key: '③ path + rewrite', value: '去掉前缀后的路径', role: '加工后接在 target 后面' }
      ],
      wrongGuesses: [
        {
          title: '猜测 1：「把 /dev-api 这一段换成 target」',
          reason: '错在“换成”。',
          points: [
            '真“换成”会得到 http://192.168.1.21:55055//admin-api/...（双斜杠）。',
            '实际是：删除发生在路径上，target 加在地址最前面，是两件事。',
            "只有写 path.replace(/^\\/dev-api/, '/api') 时才是真的替换成 /api。",
            "rewrite 的第二个参数才是“替换内容”，这里传 ''，所以是删除。"
          ]
        },
        {
          title: '猜测 2：「把 http://localhost:80/dev-api 整段替换」',
          reason: '错在“整段”。',
          points: [
            'rewrite 的入参 path 只有路径部分，不包含协议、域名、端口，所以根本不存在这个字符串可供替换。',
            '真整段替换的话路径就全丢了，后端必然报错。'
          ]
        }
      ],
      expressRows: [
        { code: "proxy 的键 /dev-api", analogy: '快递单上的分拣标签：“凡是贴这个标签的，走我这趟车”' },
        { code: 'rewrite 剪掉标签', analogy: '分拣员撕掉那张标签纸（内部用的，收件人不需要看到）' },
        { code: 'target', analogy: '这趟车开往的仓库地址' },
        { code: '剩下的 path', analogy: '包裹上真正的收件人门牌号（/admin-api/...）' },
        { code: 'changeOrigin', analogy: '到了仓库门口，改成仓库自己的名字登记' }
      ],
      relationItems: [
        { color: '#409EFF', html: '<b>target</b> 管“请求发到哪台机器”' },
        {
          color: '#E6A23C',
          html: '<b>changeOrigin</b> 管“请求头 <code>Host</code> 怎么写”，<b>只改这一行头</b>，其它头（Cookie / Authorization / User-Agent）原样透传。两者作用互不影响：改了 target 不代表 <code>Host</code> 跟着改，改了 <code>Host</code> 也不代表请求目标变了'
        },
        {
          color: '#67C23A',
          html: '<b>rewrite 的 <code>^</code></b> 是正则起始锚点，“必须从<b>开头</b>匹配”。不加 <code>^</code> 时，<code>replace</code> 会替换<b>第一个出现的位置</b>（不管在哪），像 <code>/foo/dev-api/bar</code> 或 query 里带 <code>/dev-api</code> 的都会被错误去掉，已经出现的 bug 极难排查'
        },
        {
          color: '#909399',
          html: "<code>new RegExp('^' + 变量)</code> 是拼接写法，因为前缀来自环境变量（dev 是 <code>/dev-api</code>，生产是 <code>/vision-api</code>）"
        }
      ],
      checklist: [
        { q: '连得上吗？', a: 'ERR_CONNECTION_REFUSED = target 的 IP 或端口错，或服务没启动' },
        { q: 'Host 对吗？', a: '返回不相干页面或莫名 404 = changeOrigin 没开' },
        {
          q: '路径对吗？',
          a: "后端说接口不存在，但你看路径像对的 = rewrite 多削或少削，可临时加日志确认：proxy.on('proxyReq', (r) => console.log(r.path))"
        }
      ]
    };
  },
  computed: {
    currentStep() {
      return this.steps[this.activeStep];
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/breakpoints.scss';

.proxy-page {
  .proxy-wrap {
    max-width: 1180px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    padding-bottom: var(--spacing-xxl);
  }

  code {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    background: var(--surface-2);
    color: #c7254e;
    padding: 1px 6px;
    border-radius: var(--border-radius-sm);
    font-size: 0.9em;
  }

  /* ========== 头部 ========== */
  .hero {
    position: relative;
    background: linear-gradient(135deg, #3f51b5 0%, #409eff 100%);
    color: #fff;
    border-radius: var(--border-radius-lg);
    padding: 32px 36px;
    box-shadow: var(--shadow-surface-3);
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      right: -60px;
      top: -60px;
      width: 220px;
      height: 220px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
    }

    .hero-badge {
      display: inline-block;
      font-size: 12px;
      letter-spacing: 0.05em;
      padding: 4px 12px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.18);
      margin-bottom: 14px;
      position: relative;
      z-index: 1;
    }

    .hero-title {
      font-size: 30px;
      margin: 0 0 10px;
      font-weight: 700;
      position: relative;
      z-index: 1;
    }

    .hero-sub {
      font-size: 15px;
      line-height: 1.75;
      margin: 0 0 18px;
      color: rgba(255, 255, 255, 0.92);
      position: relative;
      z-index: 1;

      b {
        color: #fff;
      }

      code {
        background: rgba(255, 255, 255, 0.2);
        color: #fff;
      }
    }

    .hero-formula {
      display: inline-flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      background: rgba(0, 0, 0, 0.18);
      border: 1px solid rgba(255, 255, 255, 0.25);
      border-radius: var(--border-radius-lg);
      padding: 12px 18px;
      font-family: 'SFMono-Regular', Consolas, Menlo, monospace;
      font-size: 15px;
      position: relative;
      z-index: 1;

      .fx-key {
        font-weight: 700;
      }
      .fx-eq,
      .fx-plus {
        color: rgba(255, 255, 255, 0.7);
      }
      .fx-target {
        color: #ffe08a;
        font-weight: 700;
      }
      .fx-path {
        color: #a8e6a3;
        font-weight: 700;
      }
    }
  }

  /* ========== 通用区块 ========== */
  .panel {
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-surface-2);
    padding: 26px 30px;
  }

  .sec-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 20px;
    color: var(--color-text-primary);
    margin: 0 0 18px;
    font-weight: 700;
  }

  .sec-no {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: linear-gradient(135deg, #409eff, #66b1ff);
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    flex: 0 0 auto;
    box-shadow: 0 4px 10px rgba(64, 158, 255, 0.35);
  }

  .sec-lead {
    color: var(--color-text-regular);
    line-height: 1.85;
    margin: 0 0 16px;
    font-size: 15px;

    b {
      color: var(--color-text-primary);
    }
  }

  /* ========== 表格 ========== */
  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14.5px;
  }

  .cfg-table,
  .express-table {
    th {
      background: var(--surface-2);
      color: var(--color-text-primary);
      text-align: left;
      padding: 12px 16px;
      font-weight: 600;
      white-space: nowrap;
    }

    td {
      padding: 12px 16px;
      border-top: 1px solid var(--border-color-lighter);
      color: var(--color-text-regular);
      vertical-align: top;
      line-height: 1.7;
    }

    tbody tr:hover {
      background: rgba(64, 158, 255, 0.04);
    }
  }

  .cfg-table .cfg-key {
    font-weight: 600;
    color: var(--color-text-primary);
    white-space: nowrap;
  }

  .express-table .ex-code {
    font-weight: 600;
    color: var(--color-text-primary);
    white-space: nowrap;

    code {
      background: transparent;
      color: var(--color-text-primary);
    }
  }

  /* ========== 提示列表 ========== */
  .note-list {
    margin: 16px 0 0;
    padding-left: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;

    li {
      position: relative;
      padding-left: 22px;
      color: var(--color-text-regular);
      line-height: 1.7;

      &::before {
        content: '•';
        position: absolute;
        left: 4px;
        color: var(--color-primary);
        font-weight: 900;
      }
    }
  }

  /* ========== 流水线 ========== */
  .req-line {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;

    .req-label {
      font-weight: 600;
      color: var(--color-text-primary);
      font-size: 14px;
    }

    .req-url {
      background: #1e293b;
      color: #a5f3fc;
      padding: 8px 14px;
      border-radius: var(--border-radius-md);
      font-size: 14px;
    }
  }

  .req-break {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 22px;

    .brk {
      font-size: 12.5px;
      padding: 5px 12px;
      border-radius: 999px;
      font-weight: 600;
    }
    .brk-proto {
      background: rgba(64, 158, 255, 0.12);
      color: #3a8ee6;
    }
    .brk-path {
      background: rgba(103, 194, 58, 0.14);
      color: #4a9e1f;
    }
    .brk-query {
      background: rgba(230, 162, 60, 0.15);
      color: #b9791f;
    }
  }

  .stepper {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }

  .step-tab {
    flex: 1 1 200px;
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: left;
    padding: 12px 14px;
    border-radius: var(--border-radius-lg);
    border: 1.5px solid var(--border-color-base);
    background: var(--surface-1);
    cursor: pointer;
    font-family: inherit;
    transition: all var(--transition-duration-fast) var(--transition-timing-base);

    &:hover {
      border-color: var(--color-primary-light);
      transform: translateY(-1px);
    }

    &.active {
      border-color: var(--accent, #409eff);
      background: rgba(64, 158, 255, 0.08);
      box-shadow: 0 6px 16px rgba(64, 158, 255, 0.18);
    }

    .step-badge {
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: var(--surface-2);
      color: var(--color-text-regular);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 13px;
      flex: 0 0 auto;
    }

    &.active .step-badge {
      background: var(--accent, #409eff);
      color: #fff;
    }

    &.done .step-badge {
      background: rgba(103, 194, 58, 0.2);
      color: #4a9e1f;
    }

    .step-icon {
      font-size: 16px;
    }

    .step-name {
      font-size: 13.5px;
      color: var(--color-text-primary);
      font-weight: 600;
      line-height: 1.3;
    }
  }

  .step-detail {
    border: 1px solid var(--border-color-lighter);
    border-left: 4px solid var(--accent, #409eff);
    border-radius: var(--border-radius-lg);
    padding: 20px 22px;
    background: var(--surface-2);

    .step-detail-head {
      display: flex;
      align-items: baseline;
      gap: 10px;
      margin-bottom: 12px;
    }

    .step-detail-no {
      font-size: 12.5px;
      font-weight: 700;
      color: var(--accent, #409eff);
      background: rgba(64, 158, 255, 0.12);
      padding: 3px 10px;
      border-radius: 999px;
    }

    .step-detail-title {
      font-size: 16px;
      font-weight: 700;
      color: var(--color-text-primary);
    }

    .step-p {
      margin: 0 0 10px;
      line-height: 1.8;
      color: var(--color-text-regular);
      font-size: 14.5px;
    }
  }

  .code-block {
    margin: 14px 0;
    background: #1e293b;
    border-radius: var(--border-radius-lg);
    padding: 16px 18px;
    overflow-x: auto;

    code {
      background: none;
      color: #e2e8f0;
      padding: 0;
      font-size: 13.5px;
      line-height: 1.7;
      white-space: pre;
    }
  }

  .io-row {
    display: flex;
    align-items: stretch;
    gap: 12px;
    margin-top: 10px;
    flex-wrap: wrap;
  }

  .io-box {
    flex: 1 1 220px;
    border-radius: var(--border-radius-lg);
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    code {
      background: none;
      color: var(--color-text-primary);
      font-size: 13.5px;
      padding: 0;
      word-break: break-all;
    }
  }

  .io-in {
    background: rgba(64, 158, 255, 0.08);
    border: 1px solid rgba(64, 158, 255, 0.25);

    .io-tag {
      color: #3a8ee6;
    }
  }

  .io-out {
    background: rgba(103, 194, 58, 0.08);
    border: 1px solid rgba(103, 194, 58, 0.28);

    .io-tag {
      color: #4a9e1f;
    }
  }

  .io-tag {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .io-arrow {
    display: flex;
    align-items: center;
    font-size: 22px;
    color: var(--color-text-secondary);
  }

  /* ========== 错误猜测 ========== */
  .wrong-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }

  .wrong-card {
    background: rgba(245, 108, 108, 0.05);
    border: 1px solid rgba(245, 108, 108, 0.3);
    border-radius: var(--border-radius-lg);
    padding: 18px 20px;

    .wrong-head {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
    }

    .wrong-x {
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: var(--color-danger);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      flex: 0 0 auto;
    }

    .wrong-title {
      font-size: 15px;
      font-weight: 700;
      color: #c0392b;
      line-height: 1.4;
    }

    .wrong-reason {
      display: inline-block;
      font-weight: 700;
      color: var(--color-danger);
      margin-bottom: 10px;
    }

    .wrong-points {
      margin: 0;
      padding-left: 18px;

      li {
        color: var(--color-text-regular);
        line-height: 1.75;
        margin-bottom: 8px;
        font-size: 14px;
      }
    }
  }

  /* ========== 快递类比 ========== */
  .memo-box {
    margin-top: 16px;
    background: linear-gradient(135deg, rgba(64, 158, 255, 0.08), rgba(103, 194, 58, 0.08));
    border: 1px dashed var(--color-primary-light);
    border-radius: var(--border-radius-lg);
    padding: 16px 20px;
    line-height: 1.9;
    color: var(--color-text-regular);
    font-size: 14.5px;

    b {
      color: var(--color-text-primary);
    }

    .memo-em {
      color: var(--color-primary-dark);
      font-weight: 700;
    }
  }

  /* ========== 三者关系 ========== */
  .relation-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;

    li {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      line-height: 1.8;
      color: var(--color-text-regular);
      font-size: 14.5px;
    }

    .rel-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-top: 8px;
      flex: 0 0 auto;
    }

    .rel-text b {
      color: var(--color-text-primary);
    }
  }

  /* ========== 排查口诀 ========== */
  .check-list {
    list-style: none;
    margin: 16px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .check-item {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    background: var(--surface-2);
    border-radius: var(--border-radius-lg);
    padding: 14px 18px;
  }

  .check-order {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #409eff, #66b1ff);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    flex: 0 0 auto;
  }

  .check-q {
    font-weight: 700;
    color: var(--color-text-primary);
    margin-bottom: 4px;
  }

  .check-a {
    color: var(--color-text-regular);
    line-height: 1.7;
    font-size: 14px;
    word-break: break-word;
  }

  .foot-note {
    margin-top: 16px;
    padding: 12px 16px;
    border-radius: var(--border-radius-md);
    background: rgba(230, 162, 60, 0.1);
    border-left: 3px solid var(--color-warning);
    color: var(--color-text-regular);
    font-size: 13.5px;
    line-height: 1.7;
  }

  /* ========== 响应式 ========== */
  @include bp-middle {
    .hero {
      padding: 28px 28px;

      .hero-title {
        font-size: 27px;
      }
    }

    .panel {
      padding: 22px 24px;
    }
  }

  @include bp-small {
    .proxy-wrap {
      gap: 16px;
    }

    .hero {
      padding: 24px 22px;

      .hero-title {
        font-size: 23px;
      }
      .hero-sub {
        font-size: 14px;
      }
      .hero-formula {
        font-size: 13.5px;
      }
    }

    .panel {
      padding: 18px 18px;
    }

    .sec-title {
      font-size: 18px;
    }

    .wrong-grid {
      grid-template-columns: 1fr;
    }

    .step-tab {
      flex-basis: 100%;
    }

    .io-row {
      flex-direction: column;
    }

    .io-arrow {
      align-self: center;
      transform: rotate(90deg);
    }

    .cfg-table,
    .express-table {
      font-size: 13px;

      th,
      td {
        padding: 10px 12px;
      }
    }
  }
}
</style>
